import { Box } from '@everlywell/ui-kit';
import type { BoxProps } from '@everlywell/ui-kit';
import * as React from 'react';
import type { DetailedHTMLProps, VideoHTMLAttributes } from 'react';

import {
  useEmbeddedVideo,
  type APITypes,
  type PlyrInstance,
  type PlyrOptions,
  type VideoSource,
} from '../../hooks/useEmbeddedVideo';

type ReactVideoProps = DetailedHTMLProps<
  VideoHTMLAttributes<HTMLVideoElement>,
  HTMLVideoElement
>;
type BaseProps = Omit<ReactVideoProps, 'ref'> & {
  options?: PlyrOptions | null;
  onReady?: (player: PlyrInstance) => void;
  sx?: BoxProps['sx'];
};

export type PlyrProps =
  | (BaseProps & { kind: 'withSource'; source: VideoSource })
  | (BaseProps & { kind: 'noSource'; source?: null | undefined });

const VideoPlayer = React.forwardRef<APITypes, PlyrProps>((props, ref) => {
  const { kind, options = null, onReady, className, sx, ...rest } = props;

  const videoRef = useEmbeddedVideo(
    ref,
    kind === 'withSource'
      ? { kind, source: props.source, options, onReady }
      : { kind, options, onReady },
    [kind, options],
  );

  const resolvedClassName = ['plyr', className].filter(Boolean).join(' ');

  return (
    <Box sx={sx}>
      <video ref={videoRef} className={resolvedClassName} {...rest} />
    </Box>
  );
});

// PropTypes removed in favor of TypeScript-only validation
// TypeScript's PlyrProps union type provides compile-time safety

export default VideoPlayer;
